import React, { useState, useEffect } from "react"
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from "recompose";
import { connect } from "react-redux";
import { push } from 'react-router-redux';
import { Content, LoadingState, ErrorState } from "components"
import { PurchaseDetails } from '../components'
import { CouponForm, BillingForm } from '../forms'
import * as apiActions from 'api-actions'
import * as effects from 'effects'
import { selectors as orderSelectors } from "../reducer";
import { selectors as activitySelectors } from '../../activities/reducer'
import { scrollToTop, trackPurchaseFromOrder, trackBeginCheckout, trackAddPaymentInfo, navigateAwayPromptHook } from 'utils'
import * as flashActions from 'redux-flash'
import { filter, isEmpty, sumBy, flatMap } from "lodash";
import AddOnDonationForm from "../forms/AddOnDonationForm";
import { GpiWrapper } from "components/Gpi";
import { submit } from 'redux-form';

const propTypes = {
  cart: Types.cart,
  fetchCart: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,
  activities: PropTypes.arrayOf(Types.activity),
  fetchActivities: PropTypes.func.isRequired,
  validateCoupon: PropTypes.func.isRequired,
  deleteCoupon: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  verifyRecaptcha: PropTypes.func.isRequired,
  order: Types.ticketingOrder,
  fetchOrder: PropTypes.func.isRequired,
  deleteOrder: PropTypes.func.isRequired,
  submitBillingForm: PropTypes.func.isRequired
}

const defaultProps = {}

function Payment(
    {
      cart,
      fetchCart,
      updateCart,
      activities,
      fetchActivities,
      validateCoupon,
      deleteCoupon,
      flashErrorMessage,
      push,
      verifyRecaptcha,
      fetchOrder,
      order,
      deleteOrder,
      submitBillingForm
    }) {
  const [error, setError] = useState(null)
  const [gpiTokenData, setGpiTokenData] = useState(null)
  const [cartTokenParam,] = useState(() => {
    return new URLSearchParams(window.location.search).get('cartToken')
  })
  const orderToken = new URLSearchParams(window.location.search).get('orderToken')
  const editMode = orderToken && order && true

  useEffect(() => {
    const token = cartTokenParam || (cart ? cart.token : null)
    fetchCart(token)
        .catch(() => setError('An error occurred while fetching your cart.'))
    fetchActivities({viewAll: true})
        .catch(() => setError('An error occurred while loading your activities.'))
  }, [])

  useEffect(() => {
    if (cart && isEmpty(cart.lineItems)) push('/activities/cart')
  }, [cart])

  useEffect(() => {
    if(orderToken) {
      fetchOrder(orderToken).catch(() => {
        if (!error) setError("Could not load order.");
      });
    }
  }, [orderToken])

  useEffect(() => {
    trackBeginCheckout(cart)
    trackAddPaymentInfo(cart)
  }, []);

  if (!cart || !activities || (orderToken && !order)) return <LoadingState/>
  if (error) return <ErrorState message={error}/>

  const skipPaymentDetails = isRefundOrNoPaymentRequired()

  function isRefundOrNoPaymentRequired() {
    // If not a rebooking (editMode) situation, keep original logic for skipping payment details if cart total is 0
    if (!editMode) {
      return cart.total <= 0
    }

    const currentCartTotal = sumBy(
      flatMap(cart.lineItems, "lineItemTickets"),
      (lineItemTicket) => parseFloat(lineItemTicket.price) * lineItemTicket.quantity
    )

    const originalTotalPaid = sumBy(
        flatMap(order.originalOrder.cart.lineItems, "lineItemTickets"),
        (lineItemTicket) => parseFloat(lineItemTicket.price) * lineItemTicket.quantity
    )
    const totalDiff = currentCartTotal - originalTotalPaid
    if (totalDiff <= 0) return true
    return false
  }

  navigateAwayPromptHook(editMode, () => {
    deleteOrder(order)
  })

  const handleGpiTokenSuccess = ({ cardToken, expirationDate, lastFourDigits, cardType }) => {
    // Store GPI token data for later use by form submission
    setGpiTokenData({ cardToken, expirationDate, lastFourDigits, cardType })

    // Trigger form validation and submission through Redux Form
    submitBillingForm()
  }

  return (
      <Content>
        <div className="ticket-details">
          <PurchaseDetails
              cart={cart}
              updateCart={updateCart}
              activities={activities}
              deleteCoupon={deleteCoupon}
              order={order}
          />
          {
            !editMode && (
              <CouponForm
                  onSubmit={(params) => {
                    validateCoupon(cart, params)
                        .catch((result) => {
                          scrollToTop()
                          const message = result.errors ? result.errors.message : 'This coupon code could not be applied based on your selections.'
                          flashErrorMessage(message)
                        })
                  }}
              />
            )
          }
          {
            !editMode && (
              <AddOnDonationForm
                  onSubmit={({donationAmount}) => {
                    updateCart(cart, {donationAmount})
                  }}
              />
            )
          }
        </div>
        <GpiWrapper
            onTokenSuccess={handleGpiTokenSuccess}
            gpiFormHasBeenRendered={!skipPaymentDetails}
        >
          <BillingForm
              cart={cart}
              skipPaymentDetails={skipPaymentDetails}
              updateCart={updateCart}
              onSubmit={(params) => {
                const timedLineItems = filter(cart.lineItems, {timed: true})
                let ticketingOrderParams = { ...params, donationAmount: cart.donationAmount }

                // Add GPI token data if available
                if (gpiTokenData) {
                  ticketingOrderParams = { ...ticketingOrderParams, ...gpiTokenData }
                }

                if(orderToken && order && order.bookingId) {
                  ticketingOrderParams['bookingId'] = order.bookingId
                  ticketingOrderParams['orderToken'] = orderToken
                }

                // Check if recaptcha is disabled
                if (process.env.DISABLE_RECAPTCHA === 'true') {
                  if (!isEmpty(timedLineItems)) {
                    return createHolds(timedLineItems)
                        .then(() => {
                          return effects.createTicketingOrder(cart, ticketingOrderParams, skipPaymentDetails, editMode)
                        })
                  } else {
                    return effects.createTicketingOrder(cart, ticketingOrderParams, skipPaymentDetails, editMode)
                  }
                } else {
                  return verifyRecaptcha(window.grecaptcha.getResponse(), cart.id)
                      .then(() => {
                        if (!isEmpty(timedLineItems)) {
                          return createHolds(timedLineItems)
                              .then(() => {
                                return effects.createTicketingOrder(cart, ticketingOrderParams, skipPaymentDetails, editMode)
                              })
                        } else {
                          return effects.createTicketingOrder(cart, ticketingOrderParams, skipPaymentDetails, editMode)
                        }
                      })
                }
              }}
              onSubmitSuccess={(success) => {
                const order = success.data.attributes
                const { cart } = order
                // Clear out cart and order from redux store
                updateCart({})
                trackPurchaseFromOrder(order, cart)
                push(`/orders/confirmation/${order.token}`)
              }}
              onSubmitFail={(error) => {
                scrollToTop()
                flashErrorMessage(error.message || 'Your order could not be submitted. Please check your information and try again.')
              }}
              recaptchaSiteKey={process.env.GOOGLE_RECAPTCHA_CLIENT_KEY}
          />
        </GpiWrapper>
      </Content>
  )
}

const createHolds = async (lineItems) => {
  const holds = lineItems.map(lineItem => effects.holdTickets(lineItem))
  return Promise.all(holds)
}


function mapStateToProps(state) {
  return {
    activities: activitySelectors.activities(state),
    cart: orderSelectors.cart(state),
    order: orderSelectors.ticketingOrder(state)
  }
}

const mapDispatchToProps = {
  fetchCart: apiActions.fetchCart,
  updateCart: apiActions.updateCart,
  fetchActivities: apiActions.fetchActivities,
  validateCoupon: apiActions.validateCoupon,
  deleteCoupon: apiActions.deleteCoupon,
  verifyRecaptcha: apiActions.verifyRecaptcha,
  flashErrorMessage: flashActions.flashErrorMessage,
  push,
  fetchOrder: apiActions.fetchTicketingOrder,
  deleteOrder: apiActions.deleteOrder,
  submitBillingForm: () => submit('billing-form')
}

Payment.defaultProps = defaultProps
Payment.propTypes = propTypes

export default compose(connect(mapStateToProps, mapDispatchToProps))(
    Payment
)
